"use client"
import {
  useState,
  useRef,
  ReactNode,
  useLayoutEffect,
  useEffect,
  Fragment,
} from "react"
import { createPortal } from "react-dom"
import {
  DropdownMenuContainer,
  DropdownMenuWrapper,
  DropdownMenuGroup,
  StyledDropdownMenuItem,
  DropdownMenuItemStatus,
  DropdownMenuItemLabel,
} from "./Styled"
import { IDropdownItemGroup, ITargetRect } from "./types"
import { getTargetRect } from "./utils"

const CloseIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M6.06694 5.18306C5.82286 4.93898 5.42714 4.93898 5.18306 5.18306C4.93898 5.42714 4.93898 5.82286 5.18306 6.06694L9.11612 10L5.18306 13.9331C4.93898 14.1771 4.93898 14.5729 5.18306 14.8169C5.42714 15.061 5.82286 15.061 6.06694 14.8169L10 10.8839L13.9331 14.8169C14.1771 15.061 14.5729 15.061 14.8169 14.8169C15.061 14.5729 15.061 14.1771 14.8169 13.9331L10.8839 10L14.8169 6.06694C15.061 5.82286 15.061 5.42714 14.8169 5.18306C14.5729 4.93898 14.1771 4.93898 13.9331 5.18306L10 9.11612L6.06694 5.18306Z"
      fill="#69757D"
    />
  </svg>
)

const SelectedIcon = () => (
  <svg
    style={{ flexShrink: 0 }}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
  >
    <path
      d="M9.55018 15.15L18.0252 6.675C18.2252 6.475 18.4585 6.375 18.7252 6.375C18.9918 6.375 19.2252 6.475 19.4252 6.675C19.6252 6.875 19.7252 7.11267 19.7252 7.388C19.7252 7.66333 19.6252 7.90067 19.4252 8.1L10.2502 17.3C10.0502 17.5 9.81685 17.6 9.55018 17.6C9.28351 17.6 9.05018 17.5 8.85018 17.3L4.55018 13C4.35018 12.8 4.25418 12.5627 4.26218 12.288C4.27018 12.0133 4.37451 11.7757 4.57518 11.575C4.77585 11.3743 5.01351 11.2743 5.28818 11.275C5.56285 11.2757 5.80018 11.3757 6.00018 11.575L9.55018 15.15Z"
      fill="#0FBDD4"
    />
  </svg>
)


const POPOVER_OFFSET_Y = 8

const DropdownContainer = ({
  children,
  x,
  y,
  contentRef,
}: {
  children: ReactNode
  x: number
  y: number
  contentRef: any
}) => {
  return (
    <DropdownMenuContainer>
      <div
        style={{
          position: "absolute",
          left: x,
          top: y,
          zIndex: "var(--z-index-popover)",
        }}
      >
        <div style={{ position: "relative" }}>
          <DropdownMenuWrapper
            ref={contentRef}
            className="dropdown-menu-wrapper"
          >
            <div className="dropdown-menu-content">{children}</div>
          </DropdownMenuWrapper>
        </div>
      </div>
    </DropdownMenuContainer>
  )
}

export const DropdownRenderer = ({
  groupItems,
  targetRect,
  onChange,
  value,
  onClose,
  onHover,
  offsetX = 0,
  triggerRef,
}: {
  groupItems: IDropdownItemGroup[]
  targetRect: ITargetRect
  onChange?: (value?: number | string) => void
  value?: number | string
  onClose?: () => void
  onHover?: ({ value, rect }: { value: any; rect?: ITargetRect }) => void
  offsetX?: number
  triggerRef: React.RefObject<HTMLSpanElement>
}) => {
  const menuContentRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState({
    x: targetRect ? targetRect.left : 0,
    y: targetRect ? targetRect.bottom + POPOVER_OFFSET_Y : 0,
  })

  useLayoutEffect(() => {
    if (menuContentRef.current && targetRect) {
      const menuContentRect = menuContentRef.current.getBoundingClientRect()

      const centerX = targetRect.left + targetRect.width / 2

      let x = offsetX + centerX - menuContentRect.width / 2 // center align

      setPosition({
        x,
        y: targetRect.bottom + POPOVER_OFFSET_Y,
      })
    }
  }, [])

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        menuContentRef.current &&
        !menuContentRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        onClose?.()
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [onClose])

  return createPortal(
    <DropdownContainer
      x={position.x}
      y={position.y}
      contentRef={menuContentRef}
    >
      <Fragment>
        <StyledDropdownMenuItem
          $isClearOption
          onClick={() => {
            onChange?.(undefined)
            onClose?.()
          }}
        >
          <DropdownMenuItemStatus style={{ marginLeft: -6 }}>
            <CloseIcon />
          </DropdownMenuItemStatus>
          <DropdownMenuItemLabel style={{ color: "#69757D" }}>
            Clear selection
          </DropdownMenuItemLabel>
        </StyledDropdownMenuItem>
        {groupItems.map((group, index) => {
          return (
            <DropdownMenuGroup key={index}>
              {group.map((item) => {
                const isSelected = item.value === value
                return (
                  <StyledDropdownMenuItem
                    $isSelected={isSelected}
                    className="dropdown-menu-item"
                    onMouseOver={(e) => {
                      if (onHover) {
                        const target = e.target as HTMLElement
                        const menuItem = target.closest(".dropdown-menu-item")

                        const rect =
                          menuItem && menuItem.getBoundingClientRect()
                        if (rect) {
                          onHover({ value: item.value, rect })
                        }
                      }
                    }}
                    key={item.value}
                    onClick={() => {
                      onChange?.(item.value)
                      onClose?.()
                    }}
                  >
                    <DropdownMenuItemLabel>{item.label}</DropdownMenuItemLabel>
                    <DropdownMenuItemStatus>
                      {isSelected && <SelectedIcon />}
                    </DropdownMenuItemStatus>
                  </StyledDropdownMenuItem>
                )
              })}
            </DropdownMenuGroup>
          )
        })}
      </Fragment>
    </DropdownContainer>,
    document.body,
  )
}

export default function CustomDropdown({
  groupItems,
  children,
  onChange,
  value,
  onHover,
  offsetX,
  onVisibleChange,
}: {
  groupItems: IDropdownItemGroup[]
  children: any
  value?: number | string
  onChange?: (value: any) => void
  onHover?: (value: any) => void
  offsetX?: number
  onVisibleChange?: (visible: boolean) => void
}) {
  const [targetRect, setTargetRect] = useState<ITargetRect | null>(null)
  const triggerRef = useRef<HTMLSpanElement>(null)

  const onToggleOpen = () => {
    if (!targetRect) {
      setTargetRect(getTargetRect(triggerRef))
      onVisibleChange?.(true)
    } else {
      setTargetRect(null)
      onVisibleChange?.(false)
    }
  }

  const onClose = () => {
    setTargetRect(null)
    onVisibleChange?.(false)
  }

  return (
    <>
      <span ref={triggerRef} onClick={() => onToggleOpen()}>
        {children}
      </span>
      {targetRect !== null && (
        <DropdownRenderer
          targetRect={targetRect}
          groupItems={groupItems}
          onChange={onChange}
          value={value}
          onClose={onClose}
          onHover={onHover}
          offsetX={offsetX}
          triggerRef={triggerRef}
        />
      )}
    </>
  )
}
